import type {
  CompetencyDomain,
  FrameworkItem,
  ItemTuple,
  ItemType,
  Role,
  RoleSource,
} from '../types/framework'

/** Number of items a subscriber must choose per type, per domain. */
export const PICKS_PER_TYPE = 2
export const PICKS_PER_DOMAIN = PICKS_PER_TYPE * 2

export const ITEM_TYPE_LABEL: Record<ItemType, string> = {
  characteristic: 'Characteristic',
  behaviour: 'Behaviour',
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

function toItems(tuples: ItemTuple[], type: ItemType): FrameworkItem[] {
  return tuples.map(([no, competency, criterion]) => ({
    no,
    competency,
    criterion,
    type,
  }))
}

/** Converts a transcribed role source into the shape the app consumes. */
export function buildRole(source: RoleSource): Role {
  return {
    id: source.id,
    name: source.name,
    group: source.group,
    domains: source.domains.map(
      (d): CompetencyDomain => ({
        id: `${source.id}-${slugify(d.name)}`,
        name: d.name,
        characteristic: toItems(d.characteristic, 'characteristic'),
        behaviour: toItems(d.behaviour, 'behaviour'),
      }),
    ),
  }
}

export function itemCount(role: Role): number {
  return role.domains.reduce(
    (sum, d) => sum + d.characteristic.length + d.behaviour.length,
    0,
  )
}

export function questionnaireSize(role: Role): number {
  return role.domains.length * PICKS_PER_DOMAIN
}

/** Item IDs are only unique within a role, so selections are keyed as `${roleId}:${no}`. */
export function itemKey(roleId: string, no: number): string {
  return `${roleId}:${no}`
}

export function domainItems(domain: CompetencyDomain): FrameworkItem[] {
  return [...domain.characteristic, ...domain.behaviour]
}

/** How many of a domain's items are selected, split by type. */
export function domainProgress(domain: CompetencyDomain, selected: number[]) {
  const characteristic = domain.characteristic.filter((i) =>
    selected.includes(i.no),
  ).length
  const behaviour = domain.behaviour.filter((i) =>
    selected.includes(i.no),
  ).length
  return {
    characteristic,
    behaviour,
    total: characteristic + behaviour,
    complete:
      characteristic === PICKS_PER_TYPE && behaviour === PICKS_PER_TYPE,
  }
}

/** The selected items for a domain, Characteristic first then Behaviour. */
export function selectedInDomain(
  domain: CompetencyDomain,
  selected: number[],
): FrameworkItem[] {
  return domainItems(domain).filter((i) => selected.includes(i.no))
}

/** The standard items for one domain and type: the first two in file order. */
export function standardItems(
  domain: CompetencyDomain,
  type: ItemType,
): FrameworkItem[] {
  return domain[type].slice(0, PICKS_PER_TYPE)
}

export function isStandard(domain: CompetencyDomain, item: FrameworkItem): boolean {
  return standardItems(domain, item.type).some((i) => i.no === item.no)
}

/**
 * The default questionnaire for a role: the first 2 Characteristic and
 * first 2 Behaviour items in every domain. Subscribers may swap from here.
 */
export function standardSelection(role: Role): number[] {
  return role.domains.flatMap((d) => [
    ...standardItems(d, 'characteristic').map((i) => i.no),
    ...standardItems(d, 'behaviour').map((i) => i.no),
  ])
}

/** Selected items in a domain that are not part of its standard set. */
export function substitutedInDomain(
  domain: CompetencyDomain,
  selected: number[],
): FrameworkItem[] {
  return selectedInDomain(domain, selected).filter((i) => !isStandard(domain, i))
}

export function substitutedCount(role: Role, selected: number[]): number {
  return role.domains.reduce(
    (n, d) => n + substitutedInDomain(d, selected).length,
    0,
  )
}
